import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import "./HlsPlayer.css";

function HlsPlayer({
  src,
  autoPlay = false,
  controls = true,
  firstVideo = false,
}) {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("16/9"); // Default aspect ratio
  const containerRef = useRef(null);

  useEffect(() => {
    let hls;
    setIsLoading(true);
    setHasError(false);

    if (videoRef.current) {
      const video = videoRef.current;

      // Handle native HLS support (Safari)
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;

        video.addEventListener("loadedmetadata", () => {
          setIsLoading(false);
          // Calculate aspect ratio from video dimensions
          if (video.videoWidth && video.videoHeight) {
            setAspectRatio(`${video.videoWidth}/${video.videoHeight}`);
          }
        });

        video.addEventListener("error", () => {
          setIsLoading(false);
          setHasError(true);
        });
      } else if (Hls.isSupported()) {
        // Use hls.js for other browsers
        hls = new Hls({
          // Optional configuration for better performance
          maxMaxBufferLength: 30,
          backBufferLength: 30,
          liveSyncDurationCount: 3,
          debug: false,
        });

        hls.loadSource(src);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          if (autoPlay) {
            video.play().catch((err) => console.log("Autoplay blocked:", err));
          }
          // Get video dimensions from the first level
          if (hls.levels && hls.levels.length > 0) {
            const { width, height } = hls.levels[0];
            if (width && height) {
              setAspectRatio(`${width}/${height}`);
            }
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error("HLS.js error:", data);
          if (data.fatal) {
            setIsLoading(false);
            setHasError(true);
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("Network error", data);
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("Media error", data);
                hls.recoverMediaError();
                break;
              default:
                console.error("Unrecoverable error");
                break;
            }
          }
        });
      } else {
        console.error("This browser does not support HLS.");
        setHasError(true);
        setIsLoading(false);
      }
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [src, autoPlay]);

  const retryLoad = () => {
    setHasError(false);
    setIsLoading(true);
    // Force reload by re-triggering the effect
  };

  return (
    <div
      ref={containerRef}
      className="hls-player-container"
      style={{ aspectRatio: aspectRatio }}
    >
      <video
        ref={videoRef}
        controls={controls}
        autoPlay={autoPlay}
        muted={firstVideo}
        className="hls-video-element"
      />

      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading video...</p>
        </div>
      )}

      {hasError && (
        <div className="error-overlay">
          <div className="error-icon">⚠️</div>
          <p>Failed to load video</p>
          <button onClick={retryLoad} className="retry-button">
            Retry
          </button>
        </div>
      )}
    </div>
  );
}

export default HlsPlayer;
